import { useMemo } from 'react';
import { AlertCircle, Minus, Plus, ShoppingBag } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { priceFor, pricingPending } from '@/hooks/useCustomerPricing';
import type { CatalogItem } from '@/types/domain';

interface CartSidebarProps {
  items: CatalogItem[];
  pricing: Map<string, number> | undefined;
  cart: Record<string, number>;
  locationName: string;
  onAdjust: (id: string, delta: number) => void;
  onSet: (id: string, qty: number) => void;
  onRemove: (id: string) => void;
  onSubmit?: () => void;
  onSaveDraft?: () => void;
}

interface CartLine extends CatalogItem {
  qty: number;
  unitPrice: number;
  isCustom: boolean;
}

export function CartSidebar({
  items,
  pricing,
  cart,
  locationName,
  onAdjust,
  onSet,
  onRemove,
  onSubmit,
  onSaveDraft,
}: CartSidebarProps) {
  const lines = useMemo<CartLine[]>(() => {
    const out: CartLine[] = [];
    for (const [id, qty] of Object.entries(cart)) {
      if (qty <= 0) continue;
      const item = items.find((i) => i.id === id);
      if (!item) continue;
      const { unitPrice, isCustom } = priceFor(item, pricing);
      out.push({ ...item, qty, unitPrice, isCustom });
    }
    return out;
  }, [items, pricing, cart]);

  // Customer pricing hasn't resolved yet — mask money so the list price
  // never flashes before the resolved (contract / price-book) price lands.
  const pending = pricingPending(pricing);
  const money = (v: number) => (pending ? '—' : formatUsd(v));

  const count = lines.reduce((s, l) => s + l.qty, 0);
  const subtotal = lines.reduce((s, l) => s + l.qty * l.unitPrice, 0);
  const savings = lines.reduce(
    (s, l) => s + l.qty * (l.listPrice - l.unitPrice),
    0,
  );

  return (
    <aside
      className={cn(
        'hidden lg:flex w-80 shrink-0 flex-col',
        'border-l border-brix-border bg-brix-surface/85 backdrop-blur-xl',
      )}
    >
      <div className="px-5 py-4 border-b border-brix-border">
        <div className="flex items-center justify-between mb-0.5">
          <h2 className="font-display text-lg font-semibold text-brix-ink">
            Current order
          </h2>
          <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-brix-primary-tint text-brix-primary">
            {count} {count === 1 ? 'item' : 'items'}
          </span>
        </div>
        <p className="text-xs text-brix-muted truncate">{locationName}</p>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 py-3">
        {lines.length === 0 ? (
          <div className="px-3 py-10 text-center">
            <ShoppingBag size={24} className="mx-auto mb-2 text-brix-muted" />
            <p className="text-sm text-brix-muted">Your cart is empty</p>
            <p className="text-xs mt-1 text-brix-muted">
              Click Add on items to start
            </p>
          </div>
        ) : (
          <ul className="space-y-1">
            {lines.map((line) => (
              <li
                key={line.id}
                className="group px-2 py-2 rounded-lg hover:bg-brix-primary-tint/40 transition-colors duration-200"
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: line.accentColor ?? '#6A7780' }}
                        aria-hidden
                      />
                      <p className="font-semibold text-xs leading-tight text-brix-ink truncate">
                        {line.name}
                      </p>
                    </div>
                    <p className="text-[10px] text-brix-muted">
                      {line.category} ·{' '}
                      <span className="font-mono">{money(line.unitPrice)}</span>
                    </p>
                  </div>
                  <p className="font-semibold text-xs font-mono whitespace-nowrap text-brix-ink">
                    {money(line.unitPrice * line.qty)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onAdjust(line.id, -1)}
                    aria-label={`Decrease ${line.name}`}
                    className="w-6 h-6 rounded border border-brix-border flex items-center justify-center hover:bg-brix-surface cursor-pointer transition-colors duration-200"
                  >
                    <Minus size={10} className="text-brix-ink" />
                  </button>
                  <input
                    type="number"
                    min={0}
                    value={line.qty}
                    onChange={(e) =>
                      onSet(line.id, parseInt(e.target.value || '0', 10))
                    }
                    className="w-10 text-center font-mono font-bold text-xs outline-none border border-transparent rounded py-0.5 focus-visible:border-brix-primary"
                    aria-label={`${line.name} quantity`}
                  />
                  <button
                    type="button"
                    onClick={() => onAdjust(line.id, 1)}
                    aria-label={`Increase ${line.name}`}
                    className="w-6 h-6 rounded border border-brix-border flex items-center justify-center hover:bg-brix-surface cursor-pointer transition-colors duration-200"
                  >
                    <Plus size={10} className="text-brix-ink" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemove(line.id)}
                    className="ml-auto text-[10px] opacity-0 group-hover:opacity-100 text-brix-muted hover:text-brix-warning cursor-pointer transition-all duration-200"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-5 pt-4 pb-5 border-t border-brix-border">
        <div className="space-y-1.5 mb-3">
          <Row label="Subtotal" value={money(subtotal)} />
          <Row label="Delivery" value="Included" muted />
          <Row label="Tax" value="Calc. at invoice" muted />
          {!pending && savings > 0 && (
            <Row label="Your savings" value={`-${formatUsd(savings)}`} success />
          )}
        </div>

        <div className="flex items-center justify-between py-3 border-t border-b border-brix-border mb-4">
          <p className="font-display text-sm font-semibold text-brix-ink">
            Estimated total
          </p>
          <p className="font-display text-xl font-semibold font-mono text-brix-primary">
            {money(subtotal)}
          </p>
        </div>

        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg mb-3 bg-brix-primary-tint">
          <AlertCircle size={14} className="text-brix-primary mt-0.5 shrink-0" />
          <p className="text-[11px] leading-relaxed text-brix-primary">
            <span className="font-semibold">Net 30.</span> Invoice issues after
            delivery. No payment at order time.
          </p>
        </div>

        <Button
          type="button"
          size="lg"
          className="w-full mb-2"
          disabled={lines.length === 0}
          onClick={onSubmit}
        >
          Review &amp; submit
        </Button>
        <button
          type="button"
          disabled={lines.length === 0}
          onClick={onSaveDraft}
          className="w-full py-2 rounded-xl text-xs font-semibold cursor-pointer text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Save as draft
        </button>
      </div>
    </aside>
  );
}

function Row({
  label,
  value,
  muted,
  success,
}: {
  label: string;
  value: string;
  muted?: boolean;
  success?: boolean;
}) {
  const color = muted ? 'text-brix-muted' : success ? 'text-brix-success' : 'text-brix-ink';
  return (
    <div className="flex items-center justify-between">
      <p className={cn('text-xs', color)}>{label}</p>
      <p className={cn('text-xs font-mono font-semibold', color)}>{value}</p>
    </div>
  );
}
